"use client";

import { Button } from "@/components/rac/button";
import { RelationshipDialog } from "@/components/relationship-dialog";
import { USER_RELATION_MUTATION } from "@/gql/user-mutations";
import { useAuth } from "@/hooks/use-auth";
import { useUserPreferencesStore } from "@/stores/user-preferences";
import { cn } from "@/utils/cn";
import { logger } from "@/utils/logger";
import { saveGroupPreference } from "@/utils/user-preferences";
import { useMutation } from "@apollo/client";
import { Loader2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
export interface RelationshipToggleProps {
  className?: string;
  variant?: "default" | "compact";
  onAfterSwitch?: () => void;
  onShowDialog?: (relationship: "buyer" | "seller", type: "guard" | "selection") => void;
}
export function RelationshipToggle({
  className,
  variant = "default",
  onAfterSwitch,
  onShowDialog
}: RelationshipToggleProps) {
  const {
    user,
    refetchUser
  } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [pendingRelationship, setPendingRelationship] = useState<"buyer" | "seller" | null>(null);
  // We distinguish between "guard" (no access) and "selection" (choose group) based on group count
  const [dialogType, setDialogType] = useState<"guard" | "selection">("guard");
  const [updateUserRelation] = useMutation(USER_RELATION_MUTATION, {
    // Force Apollo to refetch queries that depend on user data
    refetchQueries: ["GetUser"],
    // Add any relevant user queries
    awaitRefetchQueries: true
  });

  // Get current relationship from user relation field or groups
  const firstGroup = Array.isArray(user?.groups) ? user.groups[0] : undefined;
  const currentRelationship = user?.relation || firstGroup?.relationship || "buyer";

  // Group users by relationship
  const groupsByRelationship = useMemo(() => {
    if (!user?.groups) return {
      buyer: [],
      seller: []
    };
    return user.groups.reduce((acc, group) => {
      if (group.relationship === "buyer") {
        acc.buyer.push({
          id: group.id,
          name: group.name,
          type: group.type
        });
      } else if (group.relationship === "seller") {
        acc.seller.push({
          id: group.id,
          name: group.name,
          type: group.type
        });
      }
      return acc;
    }, {
      buyer: [] as Array<{
        id: string;
        name: string;
        type: string;
      }>,
      seller: [] as Array<{
        id: string;
        name: string;
        type: string;
      }>
    });
  }, [user?.groups]);
  const handleRelationshipSwitch = useCallback(async (newRelationship: "buyer" | "seller") => {
    if (!user || isLoading) {
      return;
    }

    // If already on the same relationship, just call the callback and return
    if (currentRelationship === newRelationship) {
      onAfterSwitch?.();
      return;
    }
    const targetGroups = groupsByRelationship[newRelationship] || [];

    // If user doesn't have any groups for the target relationship, show guard modal
    if (targetGroups.length === 0) {
      if (onShowDialog) {
        onShowDialog(newRelationship, "guard");
      } else {
        setPendingRelationship(newRelationship);
        setDialogType("guard");
        setShowDialog(true);
      }
      onAfterSwitch?.();
      return;
    }

    // If there are multiple groups, show group selection logic inside RelationshipDialog
    if (targetGroups.length > 1) {
      if (onShowDialog) {
        onShowDialog(newRelationship, "selection");
      } else {
        setPendingRelationship(newRelationship);
        setDialogType("selection");
        setShowDialog(true);
      }
      onAfterSwitch?.();
      return;
    }

    // Single group - direct switch
    setIsLoading(true);
    try {
      const {
        data
      } = await updateUserRelation({
        variables: {
          params: {
            update: {
              relationship: newRelationship
            }
          }
        }
      });
      if (data?.user?.update) {
        // Save the relationship preference (with first group of that type)
        const firstGroup_0 = targetGroups[0];
        if (firstGroup_0) {
          await saveGroupPreference(firstGroup_0.id, newRelationship);

          // Update global store so data hooks use the correct group
          if (newRelationship === "seller") {
            useUserPreferencesStore.getState().setSelectedGroup({
              selectedGroupId: firstGroup_0.id,
              relationship: "seller"
            });
          } else {
            useUserPreferencesStore.getState().setSelectedGroup({
              selectedGroupId: firstGroup_0.id,
              relationship: "buyer"
            });
          }
        }

        // Update user data in auth context first
        await refetchUser(data.user.update);
        toast.success(`Switched to ${newRelationship} mode`);

        // Call the callback after successful switch
        onAfterSwitch?.();
      } else {
        throw new Error("Failed to update relationship");
      }
    } catch (error) {
      logger.error("Failed to switch relationship:", error);
      toast.error(`Failed to switch to ${newRelationship} mode`);
    } finally {
      setIsLoading(false);
    }
  }, [user, currentRelationship, isLoading, updateUserRelation, refetchUser, groupsByRelationship, onAfterSwitch, onShowDialog]);
  if (!user) return null;
  let content;
  if (variant === "compact") {
    content = <div className={cn("flex items-center bg-primary-600 rounded-full p-[2px] relative", className)}>
				{/* Sliding indicator */}
				<div className={cn("absolute top-[2px] w-[50px] h-[36px] bg-white rounded-full transition-transform duration-200", currentRelationship === "seller" ? "translate-x-[52px]" : "translate-x-[2px]")} />

				{/* Buy button */}
				<Button variant="ghost" size="sm" onPress={() => handleRelationshipSwitch("buyer")} isDisabled={isLoading} className={cn("relative z-10 h-[36px] px-3 rounded-full text-sm font-medium transition-colors border-0 hover:bg-transparent", currentRelationship === "buyer" ? "text-neutral-900" : "text-white")}>
					Buy
				</Button>

				{/* Sell button */}
				<Button variant="ghost" size="sm" onPress={() => handleRelationshipSwitch("seller")} isDisabled={isLoading} className={cn("relative z-10 h-[36px] px-3 rounded-full text-sm font-medium transition-colors border-0 hover:bg-transparent", currentRelationship === "seller" ? "text-neutral-900" : "text-white")}>
					Sell
				</Button>

				{/* Loading indicator */}
				{isLoading && <div className="absolute inset-0 flex items-center justify-center bg-black/10 rounded-full">
						<Loader2 className="w-4 h-4 animate-spin text-white" />
					</div>}
			</div>;
  } else {
    content = <div className={cn("flex items-center space-x-2 p-2 bg-gray-100 rounded-lg", className)}>
				<Button variant={currentRelationship === "buyer" ? "default" : "outline"} size="sm" onPress={() => handleRelationshipSwitch("buyer")} isDisabled={isLoading} className="flex-1">
					{isLoading && currentRelationship !== "buyer" && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
					Buy
				</Button>
				<Button variant={currentRelationship === "seller" ? "default" : "outline"} size="sm" onPress={() => handleRelationshipSwitch("seller")} isDisabled={isLoading} className="flex-1">
					{isLoading && currentRelationship !== "seller" && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
					Sell
				</Button>
			</div>;
  }
  return <>
			{content}
			{showDialog && pendingRelationship && <RelationshipDialog isOpen={showDialog} onClose={() => {
      setShowDialog(false);
      setPendingRelationship(null);
    }} currentRelationship={currentRelationship as "buyer" | "seller"}
    // Conditional Props based on Dialog Type
    requiredRelationship={dialogType === "guard" ? pendingRelationship : undefined} targetRelationship={dialogType === "selection" ? pendingRelationship : undefined}
    //
    featureName={`${pendingRelationship === "seller" ? "Seller" : "Buyer"} Features`} description={`Switch to ${pendingRelationship} mode to access ${pendingRelationship}-specific features`} />}
		</>;
}